import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button, Alert, Row, Col, Form, Badge } from 'react-bootstrap';
import { RiArrowLeftLine } from 'react-icons/ri';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { DeleteModal } from '../../Common/DeleteModal';
import ShimmerLoader from '../../Common/ShimmerLoader';
import PlayerForm from '../../components/PlayerForm';
import { 
  getManagePlayerById,
  updatePlayerVerificationStatus,
  updatePlayerDetails,
  ManagePlayer,
  UpdateVerificationStatusRequest,
  UpdatePlayerDetailsRequest
} from '../../api/club/managePlayersService';
import { deletePlayer } from '../../api/club/playerService';
import {
  hasAnyPermission,
  hasTournamentWriteAccess,
} from '../../utils/roleBasedAccess';
import './ManagePlayers.css';
import { getImageUrl } from '../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../components/PlayerAssetPreviewModal';
import { PLAYER_ASSETS_ENABLED } from '../../constants/featureFlags';
import { downloadFileFromUrl } from '../../utils/fileDownload';

const PlayerDetail = () => {
  const navigate = useNavigate();
  const { playerId } = useParams<{ playerId: string }>();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [player, setPlayer] = useState<ManagePlayer | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Get edit mode from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const editModeFromUrl = searchParams.get('edit') === 'true';
  
  // Verification status state
  const [verificationStatus, setVerificationStatus] = useState<'approved' | 'referred_back'>('approved');
  const [remark, setRemark] = useState<string>('');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [assetPreviewType, setAssetPreviewType] = useState<PlayerAssetType | null>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canViewPlayerDetail = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.players.view',
    'tournament.players.manage',
  ]);

  const canManagePlayers = hasTournamentWriteAccess(userData, [
    'tournament.players.manage',
  ]);

  // Validation function to check if player can be approved
  const validatePlayerForApproval = (playerData: ManagePlayer): string[] => {
    const errors: string[] = [];
    
    // Check if name is present
    if (!playerData.name || playerData.name.trim() === '') {
      errors.push('Player name is required');
    }
    
    // Check if date of birth is present
    if (!playerData.dob || playerData.dob.trim() === '') {
      errors.push('Date of birth is required');
    }
    
    // Check if ID proof document is present
    const hasIdProof = playerData.documents?.some(doc => doc.type === 'id-proof');
    if (!hasIdProof) {
      errors.push('ID proof document is required');
    }
    
    // Check if consent form document is present
    const hasConsentForm = playerData.documents?.some(doc => doc.type === 'consent-form');
    if (!hasConsentForm) {
      errors.push('Consent form document is required');
    }
    
    return errors;
  };

  // Header styling (matching Player Information design)
  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    padding: '0 15px',
    minHeight: '60px',
    flexWrap: 'wrap' as const,
    position: 'relative' as const
  };

  const logoStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #e9ecef',
    flexShrink: 0
  } as const;

  const titleSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    flexWrap: 'wrap' as const,
    minWidth: '200px',
    marginLeft: '8px'
  } as const;

  useEffect(() => {
    if (!canViewPlayerDetail) {
      setLoading(false);
      return;
    }
    if (playerId) {
      loadPlayerData();
    }
  }, [playerId, canViewPlayerDetail]);

  // Set edit mode from URL parameter
  useEffect(() => {
    if (
      editModeFromUrl &&
      player &&
      ((player.verificationStatus !== 'approved') || canManagePlayers)
    ) {
      setIsEditMode(true);
    }
  }, [editModeFromUrl, player, canManagePlayers]);

  // Load player data
  const loadPlayerData = async () => {
    try {
      setLoading(true);
      setSubmitError(null);
      
      if (!playerId) {
        throw new Error('Player ID is required');
      }

      const playerData = await getManagePlayerById(playerId);
      setPlayer(playerData);
      setVerificationStatus(playerData.verificationStatus === 'pending' ? 'approved' : playerData.verificationStatus);
      
      // Debug: Log player data including documents
      console.log('🔍 Player data loaded in Manage Players:', playerData);
      console.log('🔍 Player documents:', playerData.documents);
      console.log('🔍 ID Proof document:', playerData.documents?.find(doc => doc.type === 'id-proof'));
      console.log('🔍 Consent Form document:', playerData.documents?.find(doc => doc.type === 'consent-form'));
      
      // Run validation for approval
      const errors = validatePlayerForApproval(playerData);
      setValidationErrors(errors);
      
      console.log('Player data loaded:', playerData);
    } catch (error: any) {
      console.error('Error loading player data:', error);
      setSubmitError(error.message || 'Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  // Handle player save (edit mode)
  const handleSavePlayer = async (formData: any) => {
    if (!player || !playerId) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    
    try {
      // Prepare the update data
      const updateData: UpdatePlayerDetailsRequest = {
        name: formData.name,
        dob: formData.dob,
        ageCategory: formData.ageCategory,
        parentName: formData.parentName, // Include parent name in update payload
        profilePic: formData.profilePic,
        documents: formData.documents
      };
      
      console.log('Updating player data:', updateData);
      
      // Call the update API
      const response = await updatePlayerDetails(playerId, updateData);
      
      console.log('Player update response:', response);
      
      if (response.success) {
        setSubmitSuccess('Player updated successfully');
        
        // Close edit mode
        setIsEditMode(false);
        
        // Reload player data to get updated information
        await loadPlayerData();
      } else {
        throw new Error(response.message || 'Failed to update player');
      }
      
    } catch (error: any) {
      console.error('Error updating player:', error);
      setSubmitError(error.message || 'Failed to update player');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle verification status update
  const handleVerificationStatusUpdate = async () => {
    if (!player || !playerId) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    
    try {
      const statusData: UpdateVerificationStatusRequest = {
        verificationStatus: verificationStatus,
        remark: remark.trim() || null
      };
      
      console.log('Updating verification status:', statusData);
      
      await updatePlayerVerificationStatus(playerId, statusData);
      
      setSubmitSuccess('Verification status updated successfully');
      
      // Reload player data to get updated information
      await loadPlayerData();
      
    } catch (error: any) {
      console.error('Error updating verification status:', error);
      setSubmitError(error.message || 'Failed to update verification status');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle verification status change
  const handleVerificationStatusChange = (status: 'approved' | 'referred_back') => {
    setVerificationStatus(status);
    // Clear validation errors when switching to referred_back (since referred_back doesn't require validation)
    if (status === 'referred_back') {
      setValidationErrors([]);
    } else {
      // Re-run validation when switching to approved
      if (player) {
        const errors = validatePlayerForApproval(player);
        setValidationErrors(errors);
      }
    }
  };

  // Handle delete player
  const handleDeletePlayer = () => {
    setShowDeleteModal(true);
  };

  const getAssetUrl = (type: PlayerAssetType): string => {
    if (!player) return '';
    const assetPath = type === 'qrCode' ? player.qrCode : player.playerIdCard;
    return assetPath ? getImageUrl(assetPath) : '';
  };

  const handleAssetView = (type: PlayerAssetType) => {
    setAssetPreviewType(type);
  };

  const handleAssetDownload = (type: PlayerAssetType) => {
    const assetUrl = getAssetUrl(type);
    if (!assetUrl || !player) return;

    const formattedName = player.playerId || player.name.replace(/\s+/g, '-').toLowerCase();
    const filename =
      type === 'qrCode'
        ? `${formattedName || 'player'}-qr-code.png`
        : `${formattedName || 'player'}-id-card.pdf`;

    downloadFileFromUrl(assetUrl, filename);
  };

  const confirmDelete = async () => {
    if (!player) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      console.log('Deleting player:', player._id);
      
      // Call the delete API (now uses SecureApi and shows proper error messages)
      await deletePlayer(player._id);
      
      // Success - close modal and navigate back
      setShowDeleteModal(false);
      navigate('/manage-players');
    } catch (error: any) {
      console.error('Error deleting player:', error);
      // Display the specific error message from the API
      setDeleteError(error.message || 'Failed to delete player. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/manage-players');
  };

  // Handle cancel edit mode
  const handleCancel = () => {
    setIsEditMode(false);
    setSubmitError(null);
    setSubmitSuccess(null);
    // Reset verification status to current player status
    if (player) {
      setVerificationStatus(player.verificationStatus === 'pending' ? 'approved' : player.verificationStatus);
    }
  };

  // Get profile picture URL
  const getProfilePicUrl = (profilePic: string) => {
    if (!profilePic) return '/default-profile.png';
    return getImageUrl(profilePic);
  };

  // Check access
  if (!canViewPlayerDetail) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h4>Access Denied</h4>
          <p>Only super-admin and operations-admin users can access this module.</p>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Manage Players', onClick: () => navigate('/manage-players') },
              { label: 'Player Details', active: true }
            ]}
          />
        </div>
        <ShimmerLoader type="club-details" />
      </Container>
    );
  }

  if (!player) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h4>Player Not Found</h4>
          <p>The requested player could not be found.</p>
          <Button variant="primary" onClick={handleBack}>
            Back to Player Listing
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="manage-players-detail-page">
      <Container fluid>
        {/* Breadcrumbs - Match All Clubs layout exactly */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Manage Players', onClick: () => navigate('/manage-players') },
              { label: 'Player Details', active: true }
            ]}
          />
        </div>

        {/* Form Loading Overlay */}
        {isSubmitting && (
          <div className="form-loading-overlay">
            <ShimmerLoader type="form" />
          </div>
        )}

        {/* Player Header - Matching Player Information design */}
        <div style={headerStyle}>
          <button 
            className="back-button" 
            onClick={handleBack}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6c757d',
              transition: 'background-color 0.2s ease',
              marginRight: '4px',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <RiArrowLeftLine size={20} />
          </button>
          <div style={logoStyle}>
            {player.profilePic ? (
              <img 
                src={getProfilePicUrl(player.profilePic)}
                alt="Player Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('d-none');
                }}
              />
            ) : null}
            {!player.profilePic && (
              <div className="default-profile-icon-detail">
                <i className="ri-user-line"></i>
              </div>
            )}
          </div>
          <div style={titleSectionStyle}>
            <h1 className="player-title" style={{ 
              margin: 0, 
              fontSize: '24px', 
              fontWeight: '600', 
              color: '#212529',
              lineHeight: '1.2'
            }}>
              {player.name}
            </h1>
            <span style={{
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '16px',
              padding: '6px 12px',
              borderRadius: '20px',
              border: 'none',
              display: 'inline-block',
              minWidth: '80px',
              textAlign: 'center',
              ...(player.verificationStatus === 'approved' ? {
                background: '#d4edda',
                color: '#155724'
              } : player.verificationStatus === 'referred_back' ? {
                background: '#fff3cd',
                color: '#856404'
              } : {
                background: '#fff3cd',
                color: '#856404'
              })
            }}>
              {player.verificationStatus.charAt(0).toUpperCase() + player.verificationStatus.slice(1)}
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="d-flex gap-2">
            {!isEditMode &&
              player &&
              ((player.verificationStatus !== 'approved') || canManagePlayers) && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsEditMode(true)}
                style={{
                  background: '#006EFF',
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                <i className="ri-edit-line me-1"></i>
                Edit
              </Button>
            )}
            {player.verificationStatus !== 'approved' && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeletePlayer}
                style={{
                  background: '#dc3545',
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                <i className="ri-delete-bin-line me-1"></i>
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {submitSuccess && (
          <div style={{ padding: '0 15px' }}>
            <Alert variant="success" className="mb-4" onClose={() => setSubmitSuccess(null)} dismissible>
              {submitSuccess}
            </Alert>
          </div>
        )}
        
        {submitError && (
          <div style={{ padding: '0 15px' }}>
            <Alert variant="danger" className="mb-4" onClose={() => setSubmitError(null)} dismissible>
              {submitError}
            </Alert>
          </div>
        )}

        {/* Player Form Card - Using the same card as Player Information */}
        <Card className="mb-4" style={{ margin: '0 15px' }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{isEditMode ? 'Edit Player' : 'Player Details'}</h5>
            </div>
            
            <PlayerForm
              player={player}
              teamId={player.teamInfo?._id || ''}
              teamName={player.teamInfo?.name || 'Unknown Team'}
              ageCategories={[player.ageCategory || '']}
              teams={[{ 
                value: player.teamInfo?._id || '', 
                label: player.teamInfo?.name || 'Unknown Team'
              }]}
              showTeamField={false} // Don't show team field in Manage Players
              onTeamChange={undefined} // Disable team changes
              onSubmit={handleSavePlayer}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              submitError={submitError}
              isReadOnly={
                !isEditMode ||
                (player &&
                  player.verificationStatus === 'approved' &&
                  !canManagePlayers)
              }
            />
          </Card.Body>
        </Card>

        {/* Player Assets */}
        {PLAYER_ASSETS_ENABLED && (
          <Card className="mb-4" style={{ margin: '0 15px' }}>
            <Card.Body>
              <h5 className="card-title mb-4">Player Assets</h5>
              <Row className="g-3">
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">QR Code</h6>
                        <p className="text-muted mb-0">Scan to verify player details instantly.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => handleAssetView('qrCode')}
                        disabled={!player.qrCode}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleAssetDownload('qrCode')}
                        disabled={!player.qrCode}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">Player ID Card</h6>
                        <p className="text-muted mb-0">Share or print this ID for match-day checks.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => handleAssetView('playerIdCard')}
                        disabled={!player.playerIdCard}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleAssetDownload('playerIdCard')}
                        disabled={!player.playerIdCard}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Player Approval Section - Show Badges Side by Side */}
        <Card style={{ margin: '0 15px' }}>
          <Card.Body>
            <h5 className="card-title mb-4">Player Approval</h5>
            <Row>
              <Col md={6}>
                <div className="mb-4">
                  <label className="form-label fw-medium">Status*</label>
                  <div className="d-flex gap-3">
                    <Form.Check 
                      type="radio" 
                      id="status-approved" 
                      name="verificationStatus" 
                      label={
                        <div className="d-flex align-items-center gap-2">
                          <span>Approved</span>
                          <Badge bg="success">Approved</Badge>
                        </div>
                      }
                      checked={verificationStatus === 'approved'} 
                      onChange={() => handleVerificationStatusChange('approved')} 
                      disabled={validationErrors.length > 0}
                      title={validationErrors.length > 0 ? `Cannot approve: ${validationErrors.join(', ')}` : ''}
                    />
                    <Form.Check 
                      type="radio" 
                      id="status-referred-back" 
                      name="verificationStatus" 
                      label={
                        <div className="d-flex align-items-center gap-2">
                          <span>Referred Back</span>
                          <Badge bg="warning">Referred Back</Badge>
                        </div>
                      }
                      checked={verificationStatus === 'referred_back'} 
                      onChange={() => handleVerificationStatusChange('referred_back')} 
                    />
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-4">
                  <Form.Label className="fw-medium">
                    Remarks {verificationStatus === 'referred_back' ? '*' : '(Optional)'}
                  </Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    placeholder={verificationStatus === 'referred_back' ? 'Please provide reason for referring back...' : 'Add note'} 
                    value={remark} 
                    onChange={(e) => setRemark(e.target.value)} 
                    isInvalid={verificationStatus === 'referred_back' && !remark.trim()}
                  />
                  {verificationStatus === 'referred_back' && !remark.trim() && (
                    <Form.Control.Feedback type="invalid">
                      Remarks are required when referring back a player.
                    </Form.Control.Feedback>
                  )}
                </div>
              </Col>
            </Row>
            
            {/* Validation Error Message */}
            {validationErrors.length > 0 && verificationStatus === 'approved' && (
              <Alert variant="warning" className="mb-3">
                <i className="ri-error-warning-line me-2"></i>
                <strong>Cannot approve player:</strong>
                <ul className="mb-0 mt-2">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            <div className="text-end">
              <Button 
                variant="primary" 
                onClick={handleVerificationStatusUpdate} 
                disabled={isSubmitting || 
                  (verificationStatus === 'approved' && validationErrors.length > 0) ||
                  (verificationStatus === 'referred_back' && !remark.trim())
                } 
                style={{ minWidth: '120px' }}
                title={
                  verificationStatus === 'approved' && validationErrors.length > 0 
                    ? `Cannot approve: ${validationErrors.join(', ')}` 
                    : verificationStatus === 'referred_back' && !remark.trim()
                    ? 'Remarks are required when referring back a player'
                    : ''
                }
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Player"
        message={`Are you sure you want to delete ${player?.name}? This action cannot be undone.`}
        isLoading={isDeleting}
        error={deleteError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={assetPreviewType !== null}
          assetType={assetPreviewType || 'qrCode'}
          assetUrl={assetPreviewType ? getAssetUrl(assetPreviewType) : undefined}
          playerName={player?.name}
          onClose={() => setAssetPreviewType(null)}
          onDownload={() => {
            if (!assetPreviewType) return;
            handleAssetDownload(assetPreviewType);
          }}
        />
      )}
    </div>
  );
};

export default PlayerDetail; 